import { CheckCircleIcon } from '@heroicons/react/outline'
import StrategyCallButton from './StrategyCallButton'

const COLORS = {
    primary: '#F28230',
    accent: '#ED5C45',
    dark: '#1F2937',
    dark40: 'color-mix(in oklab, #1f2937 40%, transparent)',
    dark50: 'color-mix(in oklab, #1f2937 50%, transparent)',
    dark70: 'color-mix(in oklab, #1f2937 70%, transparent)',
    dark80: 'color-mix(in oklab, #1f2937 80%, transparent)',
    light: '#FFF8EA',
}

const defaultContent = {
    number: '01',
    eyebrow: 'Managed Campaigns',
    titlePrefix: 'A direct mail team that handles ',
    titleHighlight: 'every part of the campaign.',
    description:
        "Work with our team to plan, build, and execute direct mail campaigns from end to end. We bring the strategy, audience, creative, production, and reporting together so your team doesn't have to coordinate vendors, fix list issues, or guess at what's working.",
    includedHeading: "What's included",
    includedItems: [
        'Campaign strategy & planning',
        'Audience selection & targeting',
        'List sourcing & hygiene',
        'Copy, design, & creative',
        'Format & cadence guidance',
        'Print production & mailing',
        'A/B & multivariate testing',
        'Response tracking & reporting',
    ],
    cta: {
        label: 'Book a strategy call ->',
        href: '/contact-us',
    },
    workflow: [
        {
            number: '01',
            label: 'Strategy',
            title: 'Define the audience, offer, and cadence.',
        },
        {
            number: '02',
            label: 'Creative',
            title: 'Write copy, design the piece, build A/B variants.',
        },
        {
            number: '03',
            label: 'Production',
            title: 'Print, address, and mail the campaign.',
            active: true,
        },
        {
            number: '04',
            label: 'Response',
            title: "Track results and report what's driving response.",
        },
        {
            number: '05',
            label: 'Refine & Repeat',
            title: 'Apply what we learned to the next drop.',
        },
    ],
}

function IncludedItem({ children }) {
    return (
        <div className="flex items-start gap-2">
            <CheckCircleIcon
                className="mt-0.5 h-5 w-5 shrink-0"
                style={{ color: COLORS.accent }}
            />
            <p className="text-sm" style={{ color: COLORS.dark80 }}>
                {children}
            </p>
        </div>
    )
}

function WorkflowStep({ number, label, title, active = false }) {
    const badgeClasses = active ? 'border-[#F28230]' : 'border-[#1f2937]/15'
    const panelClasses = active
        ? 'border-2 border-[#F28230]/30 bg-white shadow-sm hover:border-[#F28230]'
        : 'border border-[#1f2937]/10 bg-[#FFF8EA] hover:border-[#F28230]/40'
    const badgeText = active ? 'Active' : 'Step'
    const badgeLabelColor = active ? COLORS.accent : COLORS.dark40
    const badgeNumberColor = active ? COLORS.accent : 'rgba(31, 41, 55, 0.6)'
    const labelColor = active ? COLORS.accent : COLORS.dark50

    return (
        <div className="flex items-center gap-4">
            <div
                className={`flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-full border-2 bg-white ${badgeClasses}`}>
                <div
                    className="text-[9px] font-bold uppercase tracking-[1.2px] leading-tight"
                    style={{ color: badgeLabelColor }}>
                    {badgeText}
                </div>
                <div
                    className="text-base font-bold leading-none"
                    style={{ color: badgeNumberColor }}>
                    {number}
                </div>
            </div>

            <div
                className={`flex-1 rounded-xl px-4 py-3 transition-all duration-200 hover:translate-x-1 ${panelClasses}`}>
                <div
                    className="text-xs font-bold uppercase tracking-[1.2px]"
                    style={{ color: labelColor }}>
                    {label}
                </div>
                <div
                    className="mt-1 text-sm font-bold"
                    style={{ color: COLORS.dark }}>
                    {title}
                </div>
            </div>
        </div>
    )
}

export default function ManagedCampaignsSection(props) {
    const number = props?.number || defaultContent.number
    const eyebrow = props?.eyebrow || defaultContent.eyebrow
    const titlePrefix = props?.titlePrefix || defaultContent.titlePrefix
    const titleHighlight =
        props?.titleHighlight || defaultContent.titleHighlight
    const description = props?.description || defaultContent.description
    const includedHeading =
        props?.includedHeading || defaultContent.includedHeading
    const includedItems =
        Array.isArray(props?.includedItems) && props.includedItems.length > 0
            ? props.includedItems
            : defaultContent.includedItems
    const cta = props?.cta || defaultContent.cta
    const workflow =
        Array.isArray(props?.workflow) && props.workflow.length > 0
            ? props.workflow
            : defaultContent.workflow

    return (
        <section id="managed-campaigns" className="bg-white scroll-mt-24">
            <div className="container mx-auto px-4 py-16 lg:py-24">
                <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
                    <div className="flex flex-col gap-6">
                        <div className="flex items-center gap-3">
                            <div
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                                style={{ backgroundColor: COLORS.primary }}>
                                {number}
                            </div>
                            <div
                                className="text-xs font-bold uppercase tracking-[1.2px]"
                                style={{ color: COLORS.dark50 }}>
                                {eyebrow}
                            </div>
                        </div>

                        <h2
                            className="text-3xl font-[700] tracking-tight md:text-[44px] md:leading-[1.1]"
                            style={{ color: COLORS.dark }}>
                            {titlePrefix}
                            <span className="text-[#ed5c2f]">
                                {titleHighlight}
                            </span>
                        </h2>

                        <p
                            className="text-lg leading-8"
                            style={{ color: COLORS.dark70 }}>
                            {description}
                        </p>

                        <div className="mt-2 rounded-2xl border border-[#F28230]/15 bg-[#FFF8EA] p-6">
                            <div
                                className="mb-3 text-xs font-bold uppercase tracking-[1.2px]"
                                style={{ color: COLORS.accent }}>
                                {includedHeading}
                            </div>
                            <div className="grid grid-cols-1 gap-x-6 gap-y-2.5 lg:grid-cols-2">
                                {includedItems.map((item, index) => (
                                    <IncludedItem key={`${item}-${index}`}>
                                        {item}
                                    </IncludedItem>
                                ))}
                            </div>
                        </div>

                        {cta?.label ? (
                            <div>
                                <StrategyCallButton className="inline-flex items-center justify-center rounded-full bg-rtl-gradient px-7 py-3.5 text-base font-semibold text-white shadow-lg transition hover:opacity-95">
                                    {cta.label}
                                </StrategyCallButton>
                            </div>
                        ) : null}
                    </div>

                    <div className="flex flex-col gap-4 rounded-3xl border border-[#1f2937]/10 bg-white p-6 shadow-xl transition-shadow duration-300 hover:shadow-2xl">
                        {workflow.map((step) => (
                            <WorkflowStep
                                key={`${step.number}-${step.label}`}
                                {...step}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}
